import React from 'react';
import { motion } from 'framer-motion';
import { Language } from '@/types';

interface HeroProps {
  lang: Language;
}

interface HeadlineToken {
  text: string;
  linkHref?: string;
}

const Hero: React.FC<HeroProps> = ({ lang }) => {
  const isRtl = lang === 'ar';

  const tokensEn: HeadlineToken[] = [
    { text: 'Spatial' },
    { text: 'research', linkHref: '/research/overview' },
    { text: 'and' },
    { text: 'products', linkHref: '/products/nova' },
    { text: 'built' },
    { text: 'to' },
    { text: 'model' },
    { text: 'the' },
    { text: 'physical' },
    { text: 'world.' }
  ];

  const tokensAr: HeadlineToken[] = [
    { text: 'أبحاث', linkHref: '/research/overview' },
    { text: 'ومنتجات', linkHref: '/products/nova' },
    { text: 'الذكاء' },
    { text: 'المكاني' },
    { text: 'المصممة' },
    { text: 'لنمذجة' },
    { text: 'العالم' },
    { text: 'الحقيقي.' }
  ];

  const tokens = isRtl ? tokensAr : tokensEn;

  const description = isRtl
    ? 'فكسل هي شركة أبحاث وأمان ذكاء اصطناعي مكاني. نربط بين الفيكتور والبيكسل لبناء أنظمة ذكاء اصطناعي موثوقة وقابلة للتفسير لنظم المعلومات الجغرافية (GIS)، والاستشعار عن بعد، ونمذجة العالم الحقيقي.'
    : 'VEXL is a Spatial AI safety and research company. We bridge Vectors and Pixels to build reliable, interpretable, and steerable AI systems for GIS, remote sensing, and physical world modeling.';

  return (
    <section 
      dir={isRtl ? 'rtl' : 'ltr'} 
      className="relative min-h-[50vh] md:min-h-[55vh] flex flex-col justify-center overflow-hidden z-20 pt-24 pb-8 bg-[#05070A]"
    >
      <div
        className={`
          relative z-10 w-full max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
          ${isRtl ? 'text-right' : 'text-left'}
        `}
      >
        {/* Primary Headline Column with Word-by-Word Kinetic Reveal */}
        <div className="lg:col-span-7">
          <h1
            dir={isRtl ? 'rtl' : 'ltr'}
            className={`text-3xl md:text-5xl lg:text-[52px] font-medium text-[#EDF0F1] max-w-2xl ${
              isRtl
                ? 'font-arabic text-right leading-[1.35] tracking-normal'
                : 'text-left leading-[1.12] tracking-[-0.5px] ltr:-ml-3 md:ltr:-ml-5'
            }`}
            style={{
              fontFamily: isRtl
                ? "'Noto Sans Arabic', 'Cairo', system-ui, -apple-system, sans-serif"
                : "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif",
            }}
          >
            {tokens.map((token, index) => (
              <span
                key={`${token.text}-${index}-${lang}`}
                className={`inline-block overflow-hidden py-1 ${
                  isRtl ? 'ml-[0.32em]' : 'mr-[0.25em]'
                }`}
              >
                <motion.span
                  initial={{ y: '125%', opacity: 0 }}
                  animate={{ y: '0%', opacity: 1 }}
                  transition={{
                    duration: 0.75,
                    ease: [0.16, 1, 0.3, 1], // Luxury cubic-bezier deceleration curve
                    delay: 0.08 + index * 0.045,
                  }}
                  className="inline-block"
                >
                  {token.linkHref ? (
                    <a
                      href={token.linkHref}
                      className="underline underline-offset-[6px] decoration-[#0E93A3]/40 hover:decoration-[#0E93A3] hover:text-[#0E93A3] transition-colors"
                    >
                      {token.text}
                    </a>
                  ) : (
                    token.text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Secondary Description Column */}
        <div className="lg:col-span-5">
          <motion.p
            key={`desc-${lang}`}
            dir={isRtl ? 'rtl' : 'ltr'}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.12 + tokens.length * 0.045 + 0.08,
            }}
            className={`text-[#9AA5AC] text-base md:text-[17px] leading-[1.7] max-w-lg lg:mt-2 font-normal ${
              isRtl ? 'font-arabic text-right' : 'text-left'
            }`}
            style={{
              fontFamily: isRtl
                ? "'Noto Sans Arabic', 'Cairo', system-ui, -apple-system, sans-serif"
                : "'Inter', system-ui, sans-serif",
            }}
          >
            {description}
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
